import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { StateService, Transition } from '@uirouter/core';
import { Subscription } from 'rxjs';

import { StoreAction } from 'store/actions';
import { CimaCore, Cloudinary, Util, XmStore, XmStoreUtil } from 'core/services';
import { User } from 'store/user/models';
import { AccountSummary, LineSummary, PlanSummary, SummaryLineWithPlan } from 'store/account-summary/models';
import { Datasaver, LineDataPlan, PlanEligibility } from 'store/line/models';
import { AddonCollections } from 'store/addons/addon-collections';
import { CmsLineDataPlanCollection, CmsPlanSwitch } from 'store/cms/models';
import { PaymentState, ProgressStatus, UnlPlanSubgroupName, UnlPlanSubgroupSeg5 } from 'my-account/constants';
import { DiscountPromoCollection, DiscountPromotion } from 'store/promotions/discount-promotion/models';
import { CONFIG_TOKEN, PlanCardsStatus, unlPlanNames } from 'core/constants';
import { IXMOptions } from 'core/interfaces';
import { planEligibilityDetails } from 'store/plan-eligibility/models';
import { targetPlanSwitchDetails } from 'store/plan-eligibility/models/target-plan-switch';
import { XmcPlanSwitchService } from 'my-account/activity/shared/services/xmc-plan-switch.service';

@Component({
    selector: 'plan-switch-result',
    styleUrls: ['./result.scss'],
    templateUrl: './result.html',
    standalone: false
})
export class PlanSwitchResultComponent implements OnInit, OnDestroy {
    public subhead: string;
    public pageData: CmsPlanSwitch;
    public pageContent: CmsPlanSwitchData;
    public emailIcon: MediaImageOptions;
    public checkIcon: MediaImageOptions;
    public failureIcon: MediaImageOptions;
    public planLogoIcon: MediaImageOptions;
    public dollarIcon: MediaImageOptions;
    public alertIcon: MediaImageOptions;
    public infoIconGrey: MediaImageOptions;
    public selectedPlanData: CmsDataPlanDetails;
    public isSuccess: boolean;
    public isFailure: boolean;
    public isUnlimited: boolean;
    public planEligibility: PlanEligibility;
    public planCode: string;
    public eligiblePlanDetails: LineDataPlan;
    public pageHeader: string;
    public error: string;
    public accountSummary: AccountSummary;
    public promoResponse: DiscountPromoCollection;
    public promoContent: string;
    public isDiscountPromotion: boolean = false;
    public promotionType: string;
    public inUsePlan: LineDataPlan;
    public inUsePlanCode: string;
    public inUsePlanName: string;
    public inUsePlanData: CmsDataPlanDetails;
    public lineDataPlanCollection: CmsLineDataPlanCollection;
    public currentPlan: PlanSummary;
    public lineSummary: LineSummary;
    public user: User;
    public isSeg5UnlPlusUpgrade: boolean = false;
    public isSelectToPlus: boolean = false;
    public isStandardToProUpgrade: boolean = false;
    public isSeg6ResiUser: boolean = false;
    public isCBMUnlPremiumFlexUpgrade: boolean = false;
    public showBogoPromoLossForUnlLines: boolean = false;
    public isCbmUserSegmentFiveTabletUnlimited: boolean = false;
    public isBusinessUser: boolean;
    public inUseOnAccount: boolean;
    public planSevenPlanEligibility: targetPlanSwitchDetails;
    public eligiblePlanSevenDetails: PlanCardsDetails;
    public planDetails: planEligibilityDetails;
    public isIcopPlanSwitch: boolean;
    public allPromoContent: PromotionContent[];
    public isCancelScheduledPlan?: boolean = false;
    public isCbmGemini: boolean = false;

    private xmStore: XmStore;
    private state: StateService;
    private transition: Transition;
    private subscriptions: Subscription[] = [];
    private cimaCore: CimaCore;
    private config: IXMOptions;
    private xmcPlanSwitchService: XmcPlanSwitchService;

    private isCancelScheduledDowngrade: boolean = false;

    constructor(state: StateService, xmStore: XmStore, transition: Transition, cimaCore: CimaCore, @Inject(CONFIG_TOKEN) config: IXMOptions, xmcPlanSwitchService: XmcPlanSwitchService) {
        Object.assign(this, { state, xmStore, transition, cimaCore, config, xmcPlanSwitchService });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.isIcopPlanSwitch = this.config.ENABLE_ICOP_P1_PLANSWITCH;

        this.isCancelScheduledDowngrade = this.transition.from().name === 'account.activity.details.plan-switch.unl-tiers-cancel-downgrade';

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsPlanSwitch>(CmsPlanSwitch), (result: CmsPlanSwitch) => {
            this.pageData = result;
        }));
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (accountSummary: AccountSummary) => {
            this.accountSummary = accountSummary;
            this.isSeg6ResiUser = !this.cimaCore.isBusinessUser && this.accountSummary.level6Customer;
            this.lineSummary = this.accountSummary.getLineByKey(this.state.params.line_id);
            if (this.accountSummary) {
                const lineByKey: SummaryLineWithPlan = this.accountSummary.getLineSummaryWithPlanByKey(this.state.params.line_id);
                this.currentPlan = lineByKey.plan;
            }
            this.showBogoPromoLossForUnlLines = this.lineSummary.numberOfLinesInThePlan < 3;
        }));
        this.planCode = decodeURIComponent(this.state.params.plan_code);
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.user = user;
            this.isCbmGemini = this.config.ENABLE_CBM_GEMINI && this.user.account.isNoLevel && this.isBusinessUser;
            this.isCbmUserSegmentFiveTabletUnlimited = this.isBusinessUser && this.user.account.isSegmentFive && (this.lineSummary.isUnlBasic || this.lineSummary.isUnlPremium) && this.lineSummary.isTablet;
        }));
        // eslint-disable-next-line complexity
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
            const status: string = decodeURIComponent(this.state.params.status);
            this.isCancelScheduledPlan = this.state.params.isCancelScheduledPlan;
            this.lineDataPlanCollection = result;
            this.selectedPlanData = result.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(this.planCode));
            this.isSeg5UnlPlusUpgrade = this.accountSummary.level5Customer && ((this.selectedPlanData.planName || this.selectedPlanData.cbmPlanName) === UnlPlanSubgroupName.PLUS) && this.currentPlan.isUnlimited;
            this.isSelectToPlus = this.accountSummary.level6Customer && ((this.selectedPlanData.planName) === UnlPlanSubgroupName.MOBILE_PLUS) && this.currentPlan.isUnlimited;
            this.isStandardToProUpgrade = this.isCbmGemini && ((this.selectedPlanData.planName) === UnlPlanSubgroupName.PRO) && this.currentPlan.isUnlimited;
            this.isCBMUnlPremiumFlexUpgrade = this.isBusinessUser && this.currentPlan.isUnlimited && this.selectedPlanData.cbmPlanName === UnlPlanSubgroupName.PREMIUM_FLEX;
            this.selectedPlanData.planName = this.isCbmUserSegmentFiveTabletUnlimited ? this.selectedPlanData.tabletPlanName : this.isBusinessUser? this.selectedPlanData.cbmPlanName : this.selectedPlanData.planName;
            switch (status) {
                case ProgressStatus.IN_PROGRESS:
                    this.pageContent = this.pageData.switchPending;
                    break;
                case ProgressStatus.SUCCESS:
                    if (this.isCbmGemini) {
                        this.pageContent = this.isStandardToProUpgrade && !this.isCancelScheduledPlan ? this.pageData.successStandardToProUpgrade : this.pageData.success;
                    } else {
                        this.pageContent = this.isSelectToPlus && !this.isCancelScheduledPlan ? this.pageData.successSelectToPlus : this.pageData.success;
                    }
                    this.clearApis();
                    this.isSuccess = true;
                    break;
                default:
                    this.pageContent = this.pageData.failure;
                    this.isFailure = true;
            }
        }));

        if (this.user.account.isSegmentFive || this.user.account.isSegmentSix || this.isCbmGemini) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<planEligibilityDetails>(planEligibilityDetails), (result: planEligibilityDetails) => {
                this.planDetails = result;
                this.inUsePlanName = Util.createPascalCase(this.planDetails.currentPlan.planName);
            }));

            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<targetPlanSwitchDetails>(targetPlanSwitchDetails), (result: targetPlanSwitchDetails) => {
                this.planSevenPlanEligibility = result;
                this.eligiblePlanSevenDetails = this.planSevenPlanEligibility.selectionPage.planCards.find((plan: PlanCardsDetails) => plan.planCode === this.planCode);
                this.isUnlimited = unlPlanNames.includes(this.eligiblePlanSevenDetails.name.toUpperCase());
                this.selectedPlanData.pricePerUnit = this.eligiblePlanSevenDetails.price;
                this.inUseOnAccount = this.eligiblePlanSevenDetails.badge === PlanCardsStatus.IN_USE;
                this.pageHeader = this.isSeg6ResiUser ? this.selectedPlanData.planName : this.isUnlimited ? this.selectedPlanData.planType
                    : this.isBusinessUser && !this.isCbmGemini ? this.selectedPlanData.cbmPlanName : this.selectedPlanData.planName;

                if (this.isIcopPlanSwitch){
                    this.allPromoContent = this.planSevenPlanEligibility.allPromoContent;
                }
                this.buildIcons();
                this.buildSubhead();
            },
            (error: string) => {
                // builds page even if above subscription does not process properly 
                if (this.isCancelScheduledDowngrade) {
                    this.pageContent = this.pageData.success;
                }
    
                this.error = error;
                this.isUnlimited = (this.isUnlimited === undefined && this.selectedPlanData.planType === 'UNLIMITED');
                this.pageHeader = this.isUnlimited ? this.selectedPlanData.planType : this.isBusinessUser ? this.selectedPlanData.cbmPlanName : this.selectedPlanData.planName;
                this.buildIcons();
                this.buildSubhead();

            }));
        } else {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<PlanEligibility>(PlanEligibility, {
                filters: { lineKey: this.state.params.line_id }
            }), (planEligibility: PlanEligibility) => {
                this.planEligibility = planEligibility;
                this.eligiblePlanDetails = this.planEligibility.plans.find((plan: LineDataPlan) => plan.planCode === this.planCode);
                this.isUnlimited = this.eligiblePlanDetails.isUnlimited;
                this.selectedPlanData.pricePerUnit = this.isUnlimited ? this.eligiblePlanDetails.postSwitch.lineCost.toString() : this.eligiblePlanDetails.postSwitch.planGroupCost.toString();
                this.pageHeader = this.isUnlimited ? this.selectedPlanData.planType : this.isBusinessUser? this.selectedPlanData.cbmPlanName : this.selectedPlanData.planName;
                if ((this.isSeg5UnlPlusUpgrade || this.isCBMUnlPremiumFlexUpgrade) && this.isUnlimited) {
                    this.inUsePlan = this.planEligibility.plans.find((plan: LineDataPlan) => plan.isIneligibleInUse);
                    this.inUsePlanCode = this.inUsePlan.planCode;
                    this.inUsePlanData = this.lineDataPlanCollection.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(this.inUsePlanCode));
                    this.inUsePlanName = ((this.accountSummary.level5Customer && (this.inUsePlanData.planName || this.inUsePlanData.cbmPlanName)) === UnlPlanSubgroupName.INTRO) ? UnlPlanSubgroupSeg5.UNLIMITED : 
                        this.isBusinessUser ? this.inUsePlanData.cbmPlanName : this.inUsePlanData.planName;
                }
                this.buildIcons();
                this.buildSubhead();
            },
            (error: string) => {
                // builds page even if above subscription does not process properly 
                if (this.isCancelScheduledDowngrade) {
                    this.pageContent = this.pageData.success;
                }

                this.error = error;
                this.isUnlimited = (this.isUnlimited === undefined && this.selectedPlanData.planType === 'UNLIMITED');
                this.pageHeader = this.isUnlimited ? this.selectedPlanData.planType : this.isBusinessUser ? this.selectedPlanData.cbmPlanName : this.selectedPlanData.planName;
                this.buildIcons();
                this.buildSubhead();
            }));
        }
        if (!this.isIcopPlanSwitch) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<DiscountPromoCollection>(DiscountPromoCollection), (result: DiscountPromoCollection) => {
                this.promoResponse = result;
                this.planSwitchPromoEligible();
            }));
        } else {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<targetPlanSwitchDetails>(targetPlanSwitchDetails), (result: targetPlanSwitchDetails) => {
                this.allPromoContent = result.allPromoContent;
            }));
        }
    }

    public ngOnDestroy(): void {
        this.xmcPlanSwitchService.clearPlanSwitchState();
        Util.unsubscribeAll(this.subscriptions);
    }

    public continue(isRetry?: boolean): void {
        if (isRetry) {
            this.state.go('account.activity.details.plan-switch.in-progress', { line_id: this.state.params.line_id, plan_code: this.planCode });

            return;
        }

        this.state.go('account.activity.details', { line_id: this.state.params.line_id });
    }

    public planSwitchPromoEligible(): void {
        this.isDiscountPromotion = this.promoResponse && (this.promoResponse.isLinePromoEligible || this.promoResponse.isAccountPromoEligible || this.promoResponse.isTradeInDDAPromoEligible || this.promoResponse.isExtendedPromoDollarEligible);
        if (this.isDiscountPromotion) {
            const status: string = decodeURIComponent(this.state.params.status);
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsPlanSwitch>(CmsPlanSwitch), (resp: CmsPlanSwitch) => {
                const promo: CmsPlanSwitchMultiplePromotions = resp.planSwitchMultiplePromotions;
                this.promoContent = status === ProgressStatus.SUCCESS ? this.successPromoContent(promo) : this.inProgressPromoContent(promo);
            }));
        }
    }

    public successPromoContent(promo: CmsPlanSwitchMultiplePromotions): string {
        const tradeInDDAPromo: DiscountPromotion[] = this.promoResponse.tradeInDDAPromoEligible.filter((data: DiscountPromotion) => data.lineId === this.lineSummary.id.key || data.lineId === this.lineSummary.id.value);
        const extendedDDAPromo: DiscountPromotion[] = this.promoResponse.extendedPromoDollarEligible.filter((data: DiscountPromotion) => data.lineId === this.lineSummary.id.key || data.lineId === this.lineSummary.id.value);
        if (tradeInDDAPromo.length || extendedDDAPromo.length) {
            return this.cimaCore.isBusinessUser ? promo.successCBMDescriptionWithPromoLoss : promo.successDescriptionWithPromoLoss;
        } else {
            return this.showBogoPromoLossForUnlLines ? promo.successDescriptionWithPromoLoss : promo.successDescription;
        }
    }

    public inProgressPromoContent(promo: CmsPlanSwitchMultiplePromotions): string {
        const tradeInDDAPromo: DiscountPromotion[] = this.promoResponse.tradeInDDAPromoEligible.filter((data: DiscountPromotion) => data.lineId === this.lineSummary.id.key || data.lineId === this.lineSummary.id.value);
        const extendedDDAPromo: DiscountPromotion[] = this.promoResponse.extendedPromoDollarEligible.filter((data: DiscountPromotion) => data.lineId === this.lineSummary.id.key || data.lineId === this.lineSummary.id.value);
        if (tradeInDDAPromo.length || extendedDDAPromo.length) {
            return this.cimaCore.isBusinessUser ? promo.pendingCBMDescriptionWithPromoLoss : promo.pendingDescriptionWithPromoLoss;
        } else {
            return this.showBogoPromoLossForUnlLines ? promo.pendingDescriptionWithPromoLoss : promo.pendingDescription;
        }
    }
    
    public getPromoIcon(iconType: string): MediaImageOptions {
        switch (iconType?.toLowerCase()) {
            case 'warning':
                return this.alertIcon;
            case 'offer':
                return this.infoIconGrey;
            case 'savings':
                return this.dollarIcon;
            default:
                return this.alertIcon;
        }
    }

    private clearApis(): void {
        // clear other relevant models from store
        const clearApisList: StoreDeleteAction[] = [
            { action: StoreAction.CLEAR_LINE_COLLECTION },
            { action: StoreAction.CLEAR_STATEMENT, filters: { paymentState: PaymentState.ESTIMATED } },
            { action: StoreAction.CLEAR_TRANSACTION },
            { action: StoreAction.CLEAR_HERO_BANNER_INFO },
            { action: StoreAction.CLEAR_LINE_BANNER_INFO }
        ];

        // fetch account summary
        let action: string;

        if (this.isCbmGemini) {
            action = StoreAction.GET_SSM_ACCOUNT_SUMMARY_CBM_NO_LEVEL;
        } else if (this.user.account.isSegmentSix && !this.cimaCore.isBusinessUser) {
            action = StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT;
        } else if (this.user.account.isSegmentFive || this.user.account.isSegmentSix) {
            action = StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY;
        } else {
            action = StoreAction.GET_ACCOUNT_SUMMARY;
        }
        this.xmStore.fetchPromise<AccountSummary>(action);
        
        let planEligibilityAction: string;
        
        if (this.isCbmGemini) {
            planEligibilityAction = StoreAction.GET_CBM_PLAN_ELIGIBILITY;
        } else if (this.user.account.isSegmentSix && !this.cimaCore.isBusinessUser) {
            planEligibilityAction = StoreAction.GET_PLAN_EIGHT_ELIGIBILITY;
        } else if (this.user.account.isSegmentFive || this.user.account.isSegmentSix) {
            planEligibilityAction = StoreAction.GET_PLAN_ELIGIBILITY;
        }
        XmStoreUtil.defaultCatch(this.xmStore.fetchPromise<planEligibilityDetails>(planEligibilityAction, { params: { lineId: this.state.params.line_id } }));

        // fetch datasaver status
        XmStoreUtil.defaultCatch(this.xmStore.fetchPromise<Datasaver>(StoreAction.GET_DATASAVER_STATUS, { params: { lineKey: this.state.params.line_id } }));

        // Fetch addons only when line_id exists.
        if (this.state.params.line_id) {
            XmStoreUtil.defaultCatch(this.xmStore.fetchPromise<AddonCollections>(StoreAction.GET_ADDONS_COLLECTION, { params: { lineId: this.state.params.line_id } }));
        }

        this.xmStore.clearApis(clearApisList);
    }

    private buildIcons(): void {
        this.emailIcon = {
            mobile: Cloudinary.generateMediaFromCms(this.pageData.envelopeIcon)
        };

        this.failureIcon = {
            mobile: Cloudinary.generateMediaFromCms(this.pageData.failureIcon)
        };

        this.planLogoIcon = {
            mobile: Cloudinary.generateMediaFromCms(this.selectedPlanData.miniIcon)
        };

        this.checkIcon = {
            mobile: Cloudinary.generateMediaFromCms(this.pageData.checkIcon)
        };

        this.dollarIcon = {
            mobile: Cloudinary.generateMediaFromCms(this.pageData.dollarIcon)
        };
        this.alertIcon = {
            mobile: Cloudinary.generateMediaFromCms(this.pageData.alertIcon)
        };
        this.infoIconGrey = {
            mobile: Cloudinary.generateMediaFromCms(this.pageData.infoIconGrey)
        };
    }

    private buildSubhead(): void {
        if (this.state.params.status !== ProgressStatus.SUCCESS) {
            this.subhead = this.pageContent.subhead;

            return;
        }

        if (this.isCancelScheduledDowngrade) {
            let planName: string;
            if (this.lineSummary.isTablet) {
                planName = this.selectedPlanData.tabletPlanName;
            } else if (this.isBusinessUser) {
                planName = this.selectedPlanData.cbmPlanName;
            } else {
                planName = this.selectedPlanData.planName;
            }
            this.subhead = this.pageContent.subhead.replace('[PLAN_NAME]', this.capitalizeWord(planName));

            return;
        }

        let planName: string;
        if (this.lineSummary.isTablet) {
            planName = this.selectedPlanData.tabletPlanName;
        } else if (this.isBusinessUser && !this.isCbmGemini) {
            planName = this.selectedPlanData.cbmPlanName;
        } else {
            planName = this.selectedPlanData.planName;
        }
        this.subhead = (this.isSeg5UnlPlusUpgrade || this.isCBMUnlPremiumFlexUpgrade ||
            ((this.isSelectToPlus || this.isStandardToProUpgrade) && !this.isCancelScheduledPlan)) ? this.pageContent.subhead.replace('[NEW_PLAN_NAME]', planName)
                .replace('[CURRENT_PLAN_NAME]', this.inUsePlanName ) : this.pageContent.subhead.replace(/\[PLAN_NAME\]/g, planName);

        if (!(this.isUnlimited || (this.user.account.isSegmentFive || this.user.account.isSegmentSix || this.isCbmGemini))) {
            this.subhead = `${this.subhead}, ${this.eligiblePlanDetails.sharedDataGB} GB`;
        }
    }

    private capitalizeWord(value: string): string {
        return Util.createPascalCase(value);
    }
}
